/*global google*/
import { useCallback, useEffect, useRef } from "react"
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from "terra-draw"
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter"

export type DrawMode = "hand" | "polygon"

// Matches the styling the deprecated DrawingManager used for the lasso polygons.
const POLYGON_STYLES = {
  fillColor: "#012E23",
  fillOpacity: 0.3,
  outlineColor: "#012E23",
  outlineWidth: 2,
} as const

// Two left clicks closer than this are treated as a "double click to finish".
const DOUBLE_CLICK_MS = 300

interface UsePolygonDrawOptions {
  /** The Google map instance. The hook initialises Terra Draw once this is set. */
  map: google.maps.Map | null
  /** Current map interaction mode driven by the toolbar buttons. */
  mode: DrawMode
  /**
   * Called whenever the set of completed polygons changes (draw finished, edited,
   * dragged, deleted or cleared). The polygons are lightweight `google.maps.Polygon`
   * instances that are NOT added to the map (Terra Draw handles rendering); they exist
   * only so callers can keep using `google.maps.geometry.poly.containsLocation`.
   */
  onPolygonsChange: (polygons: google.maps.Polygon[]) => void
}

/**
 * Drop-in replacement for the lasso polygon drawing that used to rely on
 * `google.maps.drawing.DrawingManager`, which is removed from the Maps JS API as of
 * v3.65. Drawing/editing is handled by Terra Draw; completed polygons are surfaced as
 * `google.maps.Polygon` objects so the existing marker-in-polygon logic is unchanged.
 */
export const usePolygonDraw = ({ map, mode, onPolygonsChange }: UsePolygonDrawOptions) => {
  const drawRef = useRef<TerraDraw | null>(null)
  const readyRef = useRef(false)
  const lastClickRef = useRef(0)

  // Keep the latest callback / mode in refs so the init effect can stay map-only.
  const onChangeRef = useRef(onPolygonsChange)
  onChangeRef.current = onPolygonsChange
  const modeRef = useRef(mode)
  modeRef.current = mode

  // Build off-map google.maps.Polygon instances from Terra Draw's GeoJSON snapshot.
  const syncPolygons = useCallback(() => {
    const draw = drawRef.current
    if (!draw) return

    const polygons = draw
      .getSnapshot()
      .filter((feature) => feature.geometry?.type === "Polygon")
      .map((feature) => {
        const ring = (feature.geometry.coordinates as number[][][])[0]
        return new google.maps.Polygon({
          paths: ring.map(([lng, lat]) => ({ lat, lng })),
        })
      })

    onChangeRef.current(polygons)
  }, [])

  // Initialise Terra Draw once we have a map; tear it down when the map changes/unmounts.
  useEffect(() => {
    if (!map) return

    const finishCurrentPolygon = () => {
      // Terra Draw closes the active polygon on its "finish" key (Enter). Its keyboard
      // listener is attached to the map container element, so dispatch there.
      const enter = new KeyboardEvent("keyup", { key: "Enter", bubbles: true })
      map.getDiv().dispatchEvent(enter)
    }

    const polygonMode = new TerraDrawPolygonMode({
      editable: true,
      styles: POLYGON_STYLES,
      pointerEvents: {
        rightClick: true,
        contextMenu: true,
        onDragStart: true,
        onDrag: true,
        onDragEnd: true,
        // Emulate the old "double click to finish" gesture: the second click of a quick
        // double click closes the polygon instead of adding a duplicate vertex.
        leftClick: () => {
          const now = Date.now()
          if (now - lastClickRef.current < DOUBLE_CLICK_MS) {
            lastClickRef.current = 0
            setTimeout(finishCurrentPolygon, 0)
            return false
          }
          lastClickRef.current = now
          return true
        },
      },
    })

    // "Hand" mode maps to select mode: pan the map on empty space, drag/edit existing
    // polygons when grabbed.
    const selectMode = new TerraDrawSelectMode({
      flags: {
        polygon: {
          feature: {
            draggable: true,
            coordinates: { draggable: true, deletable: true },
          },
        },
      },
    })

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ lib: google.maps, map }),
      modes: [polygonMode, selectMode],
    })

    // The Google Maps adapter is only ready after its OverlayView has been added.
    draw.on("ready", () => {
      readyRef.current = true
      draw.setMode(modeRef.current === "polygon" ? "polygon" : "select")
    })
    draw.on("finish", () => syncPolygons())
    draw.on("change", (_ids, type) => {
      // 'create'/'update' fire on every vertex while drawing; only react to removals here
      // (completions and edits are handled by the 'finish' event).
      if (type === "delete") syncPolygons()
    })

    // The Google Maps adapter attaches listeners to internal map panes that only exist
    // after the map has rendered. Calling start() too early throws
    // "Cannot read properties of null (reading 'addEventListener')", so wait for the
    // first 'idle' if the map hasn't finished its initial render yet.
    let started = false
    let idleListener: google.maps.MapsEventListener | null = null

    const begin = () => {
      if (started) return
      started = true
      draw.start()
      drawRef.current = draw
    }

    if (map.getProjection()) {
      begin()
    } else {
      idleListener = google.maps.event.addListenerOnce(map, "idle", begin)
    }

    return () => {
      if (idleListener) google.maps.event.removeListener(idleListener)
      if (started) draw.stop()
      drawRef.current = null
      readyRef.current = false
      lastClickRef.current = 0
    }
  }, [map, syncPolygons])

  // Switch Terra Draw mode when the toolbar toggles between hand/polygon.
  useEffect(() => {
    const draw = drawRef.current
    if (!draw || !readyRef.current) return
    draw.setMode(mode === "polygon" ? "polygon" : "select")
  }, [mode])

  // Remove every drawn polygon (used by the "Clear" toolbar button).
  const clear = useCallback(() => {
    drawRef.current?.clear()
    onChangeRef.current([])
  }, [])

  return { clear }
}
